import React, { useState, useEffect } from "react";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { getToken } from "../../../../helpers/Jwt";

const EntryAccount = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [initialValues, setInitialValues] = useState({
        name: "",
        category: "",
        type: "",
        parent_account: null,
    });
    const [isParent, setIsParent] = useState(true);
    const [categories] = useState([
        { type: "cash_and_bank", label: "Kas & Bank" },
        { type: "accounts_receivable", label: "Akun Piutang" },
        { type: "inventory", label: "Persediaan" },
        { type: "current_assets", label: "Aktiva Lancar Lainnya" },
        { type: "fixed_assets", label: "Aktiva Tetap" },
        { type: "depreciation_amortization", label: "Depresiasi & Amortisasi" },
        { type: "other_assets", label: "Aktiva Lainnya" },
        { type: "accounts_payable", label: "Akun Hutang" },
        { type: "current_liabilities", label: "Kewajiban Lancar Lainnya" },
        { type: "long_term_liabilities", label: "Kewajiban Jangka Panjang" },        
        { type: "equity", label: "Ekuitas" },
        { type: "income", label: "Pendapatan" },
        { type: "cost_of_goods_sold", label: "Harga Pokok Penjualan" },
        { type: "expenses", label: "Beban" },
        { type: "other_income", label: "Pendapatan Lainnya" },
        { type: "other_expenses", label: "Beban Lainnya" },
        // { key: "product_buy", label: "Pembelian Produk" },
        // { key: "product_sell", label: "Penjualan Produk" },
        // { key: "cash", label: "Kas" },
        // { key: "bank", label: "Bank" }
    ]);

    const [allAccounts, setAllAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [error, setError] = useState(null);

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const PATH_ACCOUNT = "api/erpppsu/account";

    const validationSchema = Yup.object().shape({
        code: Yup.string().required("Kode Akun wajib diisi."),
        name: Yup.string().required("Nama Akun wajib diisi."),
        category: Yup.string().required("Kategori Akun wajib diisi."),
        type: Yup.string().required("Jenis Akun wajib dipilih."),
    });

    const fetchData = async (url) => {
        try {
            const response = await axios.get(url);
            return response.data;
        } catch (err) {
            setError("Gagal memuat data. Silakan coba lagi.");
            console.error(err);
            return null;
        }
    };

    useEffect(() => {
        if (!isParent) {
            setLoadingAccounts(true);
            fetchData(`${BASE_URL}${PATH_ACCOUNT}`)
                .then((accounts) => {
                    if (accounts) {
                        setAllAccounts(accounts.filter((account) => account.parent_account === null));
                    }
                })
                .finally(() => setLoadingAccounts(false));
        }
    }, [isParent]);

    useEffect(() => {
        if (id) {
            fetchData(`${BASE_URL}${PATH_ACCOUNT}/detail?id=${id}`).then((account) => {
                if (account) {
                    if (account.parent_account != null) {
                        setIsParent(false)
                    }
                    setInitialValues({
                        code: account.code || "",
                        name: account.name || "",
                        category: account.category || "",
                        type: account.type || "",                        
                        parent_account: account.parent_account || null,
                    });
                }
            });
        }
    }, [id]);

    const onSubmit = async (values, actions) => {
        try {
            
            const payload = {
                id,
                ...values,
            };

            await axios.post(`${BASE_URL}${PATH_ACCOUNT}`, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
            });
            navigate("/activity-erpppsu/accounts");
        } catch (error) {
            console.error("Error submitting form:", error);
            setError("Gagal menyimpan data. Silakan coba lagi.");
        } finally {
            actions.setSubmitting(false);
        }
    };

    return (
        <div className="mx-auto p-4">
            <div className='flex items-center justify-between p-5'>
                <h2 className="text-2xl font-bold">
                    {id ? "Edit Akun" : "Tambah Akun"}
                </h2>

                <div className='flex items-center'>
                </div>
            </div>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                enableReinitialize
            >
                {({ values, isSubmitting, setFieldValue }) => (
                    <Form>
                        <div className="gap-4 px-4 py-2">
                            <label htmlFor="code">Kode Akun</label>
                            <Field
                                name="code"
                                className="m-2 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                            />
                            <ErrorMessage name="code" component="p" className="text-red-500 text-xs mt-1" />
                        </div>
                        <div className="gap-4 px-4 py-2">
                            <label htmlFor="name">Nama Akun</label>
                            <Field
                                name="name"
                                className="m-2 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                            />
                            <ErrorMessage name="name" component="p" className="text-red-500 text-xs mt-1" />
                        </div>

                        <input
                            type="hidden"
                            name="type"
                        />
                        <div className="gap-4 px-4 py-2">
                            <label htmlFor="category">Kategori Akun</label>
                            <Field
                                as="select"
                                name="category"
                                className="my-2 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                onChange={(e) => {
                                    const selectedKey = e.target.value;
                                    const selectedCategory = categories.find((category) => category.label === selectedKey);

                                    if (selectedCategory) {
                                        setFieldValue("category", selectedCategory.label);
                                        setFieldValue("type", selectedCategory.type); // Initialize type based on the key
                                    } else {
                                        setFieldValue("category", "");
                                        setFieldValue("type", ""); // Clear type if no category is selected
                                    }
                                }}
                            >
                                <option value="">Pilih Kategori</option>
                                {categories.map((category) => (
                                    <option key={category.key} value={category.label}>
                                        {category.label}
                                    </option>
                                ))}
                            </Field>
                            <ErrorMessage name="category" component="p" className="text-red-500 text-xs mt-1" />
                        </div>

                        <div className="gap-4 px-4 py-2">
                            <label htmlFor="is_parent">Ini Akun Utama?</label>
                            <Field
                                as="select"
                                name="is_parent"
                                className="my-2 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                onChange={(e) => {
                                    const value = e.target.value === "true";
                                    setIsParent(value);
                                    setFieldValue("parent_account", value ? null : values.parent_account);
                                }}
                            >
                                <option value="" label="Pilih Jenis Akun" />
                                <option selected={isParent ? true : false } value="true">Benar, Akun Utama</option>
                                <option selected={!isParent ? true : false } value="false">Tidak, ini Sub-Akun</option>
                            </Field>
                        </div>
                        

                        {!isParent && (
                            <div className="gap-4 px-4 py-2">
                                <label htmlFor="parent_account">Akun Induk</label>
                                {loadingAccounts ? (
                                    <p>Memuat akun...</p>
                                ) : (
                                    <Field
                                        as="select"
                                        name="parent_account"
                                        className="my-2 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                    >
                                        <option value="">Pilih Akun Induk</option>
                                        {allAccounts.map((account) => (
                                            <option key={account.code} value={account.code}>
                                                {account.name}
                                            </option>
                                        ))}
                                    </Field>
                                )}
                            </div>
                        )}

                        <div className="m-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring focus:ring-blue-200 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                            >
                                {isSubmitting ? "Menyimpan..." : "Simpan"}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default EntryAccount;
